// Sheet — the phone face of every floating piece (ADR-080 §11–§12 · ADR-029:
// React Aria has no tray; the sheet is ours, one small piece on its Modal /
// Dialog). Two faces, one foundation:
//   • `bottom` — a short list: a sheet from the bottom edge with a grab bar
//     (decorative — no drag gestures: tap outside or Done), the label as its
//     heading, at most 85 % of the visible screen, finger-sized rows.
//   • `full`   — a longer list: the whole visible screen — a header (close ·
//     the label · an end action, usually Done), a PINNED slot under it (the
//     search box), the body scrolling above the keyboard, a footer slot.
// Both faces sit ABOVE the phone's keyboard: the overlay is sized to the
// foundation's `--visual-viewport-height` (React Aria's ModalOverlay sets it
// from `window.visualViewport` — iOS Safari's `dvh` ignores the keyboard, the
// visual viewport is the one reliable signal; the foundation also guards the
// pinch-zoom and the iOS blur dance), so the bottom face rises and the full
// face shrinks while the keyboard is up.
// The BEHAVIOUR is the foundation's — focus trap and return, Escape, dismiss on
// the scrim, body scroll lock, the overlay stack (a sheet over a drawer over a
// peek paints in portal order). We own 100 % of the markup + tokens. Every
// floating piece keys its face on the `md` tier (`PHONE_QUERY` — a desk gets
// the anchored panel); a story FORCES the face by its host's prop (the gate
// runs at one viewport). The hosts: the picker's field (`PickerField`, the
// open state its own), and — ADR-080 §12 — every menu, popover and calendar:
// under a React Aria trigger (`MenuTrigger` · `DialogTrigger` · `DatePicker`)
// the sheet is given NO `open`: the foundation's overlay reads the trigger's
// state (`OverlayTriggerStateContext`), so the sheet opens and closes with
// the trigger and the trigger gets its focus back.

import { useContext, useId, type ReactNode } from 'react';
import { ModalOverlay, Modal as AriaModal, Dialog, Heading, OverlayTriggerStateContext } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { icons } from './icons.js';
import { IconButton } from './IconButton.js';

export type SheetFace = 'bottom' | 'full';

/** The desk/phone threshold — the `md` tier (the shell's hamburger tier): below it every floating piece is a sheet. */
export const PHONE_QUERY = '(max-width: 47.999rem)';

export interface SheetProps {
  /** CONTROLLED by the host; omitted under a React Aria trigger (the trigger's state opens the sheet). */
  open?: boolean;
  /** The host's close; omitted under a trigger (the trigger's state closes it). */
  onClose?: () => void;
  /** The sheet's name — the heading of both faces and the dialog's accessible name. */
  title: string;
  face: SheetFace;
  /** The inside — a node, or a function given `close` (a popover's mixed content closes itself on an action). */
  children: ReactNode | ((api: { close: () => void }) => ReactNode);
  /** `full`: pinned under the header, above the scrolling body (the search box). */
  pinned?: ReactNode;
  /** `full`: the header's end control (Done). */
  endAction?: ReactNode;
  /** Both faces: a footer below the body (a host's one door). */
  footer?: ReactNode;
  /** Extra classes on the body. */
  className?: string;
}

export function Sheet({ open, onClose, title, face, children, pinned, endAction, footer, className = '' }: SheetProps) {
  const { t } = useTranslation('common');
  const headingId = useId();
  // Under a trigger the foundation's state is the sheet's; the close button and a `close`-taking inside call it.
  const triggerState = useContext(OverlayTriggerStateContext);
  const controlled = open !== undefined;
  const close = onClose ?? (() => triggerState?.close());

  return (
    <ModalOverlay
      // Controlled by the host, or (no `open`) by the trigger's state — never both (the foundation warns).
      {...(controlled
        ? {
            isOpen: open,
            onOpenChange: (isOpen: boolean) => {
              if (!isOpen) close();
            },
          }
        : {})}
      isDismissable
      // Anchored at the top and as tall as the VISIBLE viewport (the foundation's variable): the sheet's bottom edge is the keyboard's top edge.
      className="fixed inset-x-0 top-0 h-[var(--visual-viewport-height)] z-overlay flex flex-col justify-end bg-[var(--color-overlay)] data-[entering]:o-scrim-in data-[exiting]:o-scrim-out"
    >
      <AriaModal
        className={
          'flex w-full flex-col bg-[var(--color-bg-overlay)] text-[var(--color-text-primary)] shadow-modal data-[entering]:o-sheet-in data-[exiting]:o-sheet-out ' +
          (face === 'full' ? 'h-full' : 'max-h-[85%] rounded-t-2xl border-t border-[var(--color-border)]')
        }
      >
        <Dialog aria-labelledby={headingId} className="flex min-h-0 flex-1 flex-col outline-none">
          {face === 'bottom' ? (
            <div className="flex flex-col items-center px-4 pt-2 pb-1">
              {/* The grab bar is a visual cue only — no drag gesture; tap outside or Done. */}
              <span aria-hidden className="mb-2 h-1 w-10 rounded-full bg-[var(--color-border-strong)]" />
              <Heading id={headingId} slot="title" className="w-full truncate text-start text-sm font-semibold text-[var(--color-text-secondary)]">
                {title}
              </Heading>
            </div>
          ) : (
            <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-2 py-2">
              <IconButton aria-label={t('actions.close')} onClick={close} variant="ghost">
                <icons.close aria-hidden size={20} />
              </IconButton>
              <Heading id={headingId} slot="title" className="min-w-0 flex-1 truncate text-base font-semibold">
                {title}
              </Heading>
              {endAction && <div className="shrink-0">{endAction}</div>}
            </div>
          )}
          {pinned && <div className="shrink-0 border-b border-[var(--color-border)] px-3 py-2">{pinned}</div>}
          <div className={`flex min-h-0 flex-1 flex-col overflow-y-auto px-2 py-2 ${className}`}>{typeof children === 'function' ? children({ close }) : children}</div>
          {footer && <div className="shrink-0 border-t border-[var(--color-border)] px-3 py-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">{footer}</div>}
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  );
}
